import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import { connect } from 'react-redux';
import { Form, withFormik } from 'formik';

import Modal, { ModalHeader, ModalFooter } from '@lightspeed/cirrus-modal';
import Text from '@lightspeed/cirrus-text';
import { Button } from '@lightspeed/flame/Button';
import { Alert } from '@lightspeed/flame/Alert';
import Bone from '@lightspeed/cirrus-bone';

import FormField from 'cirrus/FormField';

import Autocomplete from 'pages/components/Autocomplete';
import { getData, postData, putData, clearData, clearError } from 'pages/store/actions/data';
import { entities, endpoints } from 'pages/store/schema';
import { getProcessing, getValues, getErrors } from 'pages/store/selectors';
import { actionTypes } from 'pages/store/actions/defaults';

import { paths } from './routes';
import { formatOptions } from './utils';

const hasEditVendorRight = () => window.merchantos.session.hasRight('vendors');

export const DEFAULT_VENDOR_OPTIONS = {
  NONE: {
    LABEL: 'None',
    VALUE: 0,
  },
};

export class VendorReturnModal extends Component {
  constructor(props) {
    super(props);

    this.handleVendorNewOptionClick = this.handleVendorNewOptionClick.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      isVendorReturnSaving,
      savingVendorReturnErrors,
      setSubmitting,
      getApiData,
      resetForm,
    } = this.props;

    if (prevProps.isVendorReturnSaving && !isVendorReturnSaving && !!savingVendorReturnErrors) {
      setSubmitting(false);
    }
    if (!prevProps.isOpen && this.props.isOpen) {
      resetForm();

      getApiData(endpoints.adminShop.all);
      getApiData(endpoints.vendor.all);
    }

    if ((prevProps.isOpen && !this.props.isOpen) || (!prevProps.isOpen && this.props.isOpen)) {
      this.props.clearError();
    }
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  handleVendorNewOptionClick(newOption = {}) {
    const { setFieldValue } = this.props;
    this.props.postDataVendor({
      payload: { vendor: { name: newOption.label } },
      onSuccess: response => {
        const { data: { vendors: { vendorID } = {} } = {} } = response;
        setFieldValue('vendorID', vendorID);
      },
    });
  }

  render() {
    const {
      shopID,
      vendorID,
      isOpen,
      isDisabled,
      handleCloseModal,
      isEditMode,
      isCreateMode,
      vendors = [],
      shops = [],
      loadingVendorErrors,
      loadingShopsErrors,
      savingVendorErrors,
      savingVendorReturnErrors,
      isVendorSaving,
      isLoading,
      setFieldValue,
      setFieldError,
      handleSubmit,
      isSubmitting,
      values,
      errors,
      warningMessage,
    } = this.props;
    const vendorOptions = [
      {
        value: DEFAULT_VENDOR_OPTIONS.NONE.VALUE,
        label: DEFAULT_VENDOR_OPTIONS.NONE.LABEL,
        translate: true,
      },
    ].concat(formatOptions(vendors, 'vendorID'));
    const shopOptions = formatOptions(shops, 'shopID');
    const hasErrors =
      loadingVendorErrors || loadingShopsErrors || savingVendorErrors || savingVendorReturnErrors;

    const isPristine = shopID === values.shopID && vendorID === values.vendorID;

    return (
      <Modal isOpen={isOpen} onRequestClose={handleCloseModal}>
        <Form className="cirrus">
          <ModalHeader onCloseClicked={handleCloseModal}>
            <Text bodyLarge>
              {isCreateMode ? 'New vendor return' : null}
              {isEditMode ? 'Edit vendor return' : null}
            </Text>
          </ModalHeader>
          <div className="cr-modal__body" style={{ overflow: 'visible', maxWidth: '468px' }}>
            {warningMessage ? (
              <Alert type="warning" noCloseBtn>
                <Text>{warningMessage}</Text>
              </Alert>
            ) : null}
            {hasErrors && (
              <Alert type="danger" noCloseBtn>
                <Fragment>
                  {loadingVendorErrors && (
                    <Text>There was an error while loading the vendors.</Text>
                  )}
                  {loadingShopsErrors && <Text>There was an error while loading the shops.</Text>}
                  {savingVendorErrors && <Text>There was an error while adding the vendor.</Text>}
                  {savingVendorReturnErrors && (
                    <Text>
                      {`There was an error while ${
                        isCreateMode ? 'creating' : 'updating'
                      } the vendor return.`}
                    </Text>
                  )}
                </Fragment>
              </Alert>
            )}
            <div
              style={{ width: '420px' }}
              className="cirrus cr-flex cr-width-full cr-justify-between cr-flex-col cr-ph-4 cr-mv-3"
            >
              {!isLoading && (
                <FormField
                  label="Vendor"
                  className="cr-mb-2"
                  status={errors.vendorID ? { type: 'error', message: errors.vendorID } : {}}
                >
                  <Autocomplete
                    name="vendorID"
                    options={vendorOptions}
                    value={values.vendorID}
                    isError={!!errors.vendorID}
                    isLoading={isVendorSaving}
                    creatable={hasEditVendorRight()}
                    clearable={false}
                    disabled={isDisabled}
                    onChange={value => {
                      if (value !== null) {
                        setFieldValue('vendorID', value);
                        setFieldError('vendorID');
                      }
                    }}
                    inputProps={{ maxLength: 255 }}
                    onNewOptionClick={this.handleVendorNewOptionClick}
                  />
                </FormField>
              )}
              {isLoading && <Bone width="20%" className="cr-mb-2" />}
              {isLoading && <Bone width="100%" className="cr-mb-2" height="1.875rem" />}

              {!isLoading && (
                <FormField
                  label="Shop"
                  status={errors.shopID ? { type: 'error', message: errors.shopID } : {}}
                >
                  <Autocomplete
                    name="shopID"
                    options={shopOptions}
                    value={values.shopID}
                    isError={!!errors.shopID}
                    creatable={false}
                    clearable={false}
                    disabled={isDisabled || shopOptions.length === 1}
                    onChange={value => {
                      if (value !== null) {
                        setFieldValue('shopID', value);
                        setFieldError('shopID');
                      }
                    }}
                  />
                </FormField>
              )}
              {isLoading && <Bone width="20%" className="cr-mb-2" />}
              {isLoading && <Bone className="cr-mb-2" height="1.875rem" />}
            </div>
          </div>
          <ModalFooter>
            <div className="cr-flex cr-justify-end">
              <div className="cr-mr-2">
                <Button
                  fill
                  onClick={handleCloseModal}
                  disabled={isLoading || isSubmitting}
                  data-test="vendor-return-modal-cancel-button"
                >
                  Cancel
                </Button>
              </div>
              <Button
                variant="primary"
                fill
                type="submit"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isDisabled || isLoading || isSubmitting || isPristine}
                data-test="vendor-return-modal-save-button"
              >
                <span>
                  {isCreateMode ? 'Create vendor return' : null}
                  {isEditMode ? 'Save' : null}
                </span>
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  vendors: getValues(entities.vendor)(state),
  shops: getValues(entities.adminShop)(state),
  isLoading:
    getProcessing({ entity: entities.vendor, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.adminShop, list: true, actionType: actionTypes.LOADING })(
      state
    ),
  isVendorSaving: getProcessing({
    entity: entities.vendor,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  isVendorReturnSaving: getProcessing({
    entity: entities.vendorReturns,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  loadingVendorErrors: getErrors({
    entity: entities.vendor,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  loadingShopErrors: getErrors({
    entity: entities.adminShop,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  savingVendorErrors: getErrors({
    entity: entities.vendor,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  savingVendorReturnErrors: getErrors({
    entity: entities.vendorReturns,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  getApiData: ({ url, entity, list }) => dispatch(getData({ url, entity, list })),
  postDataVendor: ({ payload, onSuccess }) =>
    dispatch(postData(endpoints.vendor.create({ payload, onSuccess }))),
  postDataVendorReturns: ({ payload, onSuccess }) =>
    dispatch(postData(endpoints.vendorReturns.create({ payload, onSuccess }))),
  putDataVendorReturns: ({ id, payload, onSuccess }) =>
    dispatch(putData(endpoints.vendorReturns.edit({ id, payload, onSuccess }))),
  clearData: () => dispatch(clearData([entities.vendor, entities.adminShop])),
  clearError: () =>
    dispatch(clearError([entities.vendor, entities.adminShop, entities.vendorReturns])),
  getListingData: ({ vendorReturnID, page, sort, filter }) =>
    dispatch(getData(endpoints.vendorReturnItem.list({ vendorReturnID, page, sort, filter }))),
});

const formSettings = {
  enableReinitialize: true,
  mapPropsToValues: ({ vendorID, shopID }) => ({
    vendorID: vendorID || DEFAULT_VENDOR_OPTIONS.NONE.VALUE,
    shopID: shopID || window.merchantos.session.getShop().shopID,
  }),
  handleSubmit: async (
    values,
    {
      props: {
        postDataVendorReturns,
        putDataVendorReturns,
        getListingData,
        isEditMode,
        isCreateMode,
        vendorReturnID,
        handleCloseModal,
      },
      setSubmitting,
      setErrors,
    }
  ) => {
    try {
      setSubmitting(true);

      if (isCreateMode) {
        await postDataVendorReturns({
          payload: {
            vendorReturn: {
              vendorID: values.vendorID,
              shopID: values.shopID,
            },
          },
          onSuccess: ({ data: { vendorReturnID: id } = {} }) => {
            handleCloseModal();
            window.PageRouter.goTo(paths.view.replace(':id', id));
          },
        });
      } else if (isEditMode) {
        await putDataVendorReturns({
          id: vendorReturnID,
          payload: {
            vendorReturn: {
              vendorID: values.vendorID,
              shopID: values.shopID,
            },
          },
          onSuccess: () => {
            handleCloseModal();
            window.flashNotifier('success', 'Vendor return updated.', 3);
            getListingData({
              vendorReturnID,
              ...qs.parse(window.location.search, { ignoreQueryPrefix: true }),
            });
          },
        });
      }
    } catch (e) {
      setSubmitting(false);
      setErrors(e.errors);
    }
  },
};

const ConnectedVendorReturnModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(withFormik(formSettings)(VendorReturnModal));

ConnectedVendorReturnModal.propTypes = {
  vendorID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shopID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ConnectedVendorReturnModal.defaultProps = {
  vendorID: '',
  shopID: '',
};

export default ConnectedVendorReturnModal;
